/**
 * NeuActions - Action type constants and helper utilities
 * 
 * Defines all actions Neu can take, their descriptions,
 * and page-specific quick action configurations.
 */

// ─── Action Type Constants ───────────────────────────────────
export const ACTION_TYPES = {
  NAVIGATE: 'navigate',
  UPDATE_SYSTEM_MESSAGE: 'updateSystemMessage',
  UPDATE_PROMPT: 'updatePrompt',
  UPDATE_JSON_SCHEMA: 'updateJsonSchema',
  SELECT_SYSTEM_MESSAGE: 'selectSystemMessage',
  OPEN_EDITOR: 'openEditor',
  CLOSE_EDITOR: 'closeEditor',
  TRIGGER_PARSE: 'triggerParse',
  SELECT_MODEL: 'selectModel',
  SHOW_NOTIFICATION: 'showNotification',
  OPEN_CHAT: 'openChat',
  CREATE_SYSTEM_MESSAGE: 'createSystemMessage',
};

// ─── Page Name Mapping ───────────────────────────────────────
export const PAGE_NAMES = {
  '/': 'Home',
  '/playground': 'Playground',
  '/system-messages': 'Instructions',
};

// External destinations surfaced on the home page
export const EXTERNAL_LINKS = {
  docs: 'https://docs.maersk.io/neurix',
  neurix: 'https://neurix.maersk.io/',
};

// ─── Action Description Builders ─────────────────────────────
const ACTION_DESCRIPTIONS = {
  [ACTION_TYPES.NAVIGATE]: (data) => `Navigated to **${PAGE_NAMES[data.path] || data.path}**`,
  [ACTION_TYPES.UPDATE_SYSTEM_MESSAGE]: () => 'Updated the **instruction** in the editor',
  [ACTION_TYPES.UPDATE_PROMPT]: () => 'Updated the **prompt** in the editor',
  [ACTION_TYPES.UPDATE_JSON_SCHEMA]: () => 'Updated the **JSON schema** in the editor',
  [ACTION_TYPES.SELECT_SYSTEM_MESSAGE]: (data) => `Selected instruction: **${data.name}**`,
  [ACTION_TYPES.OPEN_EDITOR]: () => 'Opened the **instruction editor**',
  [ACTION_TYPES.CLOSE_EDITOR]: () => 'Closed the **instruction editor**',
  [ACTION_TYPES.TRIGGER_PARSE]: () => 'Triggered **document parsing**',
  [ACTION_TYPES.SELECT_MODEL]: (data) => `Selected model: **${data.model}**`,
  [ACTION_TYPES.CREATE_SYSTEM_MESSAGE]: (data) => `Created new instruction: **${data.name}**`,
};

/**
 * Generate a human-readable summary of executed actions for chat display
 */
export function describeActions(actions) {
  if (!actions || actions.length === 0) return '';
  return actions
    .map(a => {
      const descFn = ACTION_DESCRIPTIONS[a.type];
      return descFn ? `✓ ${descFn(a.data || {})}` : null;
    })
    .filter(Boolean)
    .join('\n');
}

// ─── Page-Specific Quick Actions ─────────────────────────────
export function getQuickActions(path) {
  switch (path) {
    case '/system-messages':
      return [
        { id: 'pickPrompt', label: 'Help me pick an instruction', actionId: 'helpPickPrompt' },
        { id: 'review', label: 'Review my prompt & JSON', actionId: 'review' },
        { id: 'tips', label: 'Get optimization tips', actionId: 'tips' },
        { id: 'rewrite', label: 'Rewrite for LLM', actionId: 'rewrite' },
      ];
    case '/playground':
      return [
        { id: 'guide', label: 'How to use the Playground', actionId: 'how-to-playground' },
        { id: 'navigate-sm', label: 'Go to Instructions', actionId: 'navigateSystemMessages' },
      ];
    case '/':
      return [
        { id: 'create', label: 'Open Playground', actionId: 'navigateCreate' },
        { id: 'system', label: 'Manage instructions', actionId: 'navigateSystem' },
        { id: 'docs', label: 'Read the Docs', actionId: 'openDocs' },
        { id: 'neurix', label: 'Go to Neurix', actionId: 'openNeurix' },
      ];
    default:
      return [];
  }
}

/**
 * Get greeting text based on current page
 */
export function getPageGreeting(path) {
  switch (path) {
    case '/system-messages':
      return {
        title: "Instructions Assistant",
        subtitle: "I can review, optimize, and rewrite your prompts and JSON schemas."
      };
    case '/playground':
      return {
        title: "Playground Assistant",
        subtitle: "I'll help you extract and review structured data from your documents."
      };
    default:
      return {
        title: "Neurix Lab Assistant",
        subtitle: "Ask me anything about the platform!"
      };
  }
}

/**
 * Map quick/proactive action IDs to actual parameters for the backend
 */
export function resolveActionToMessage(actionId) {
  const editorContent = window.systemMessageEditorContent || {};
  const prompt = editorContent.system_message || '';
  const jsonSchema = editorContent.response_format || {};

  switch (actionId) {
    // System message actions
    case 'review':
      return {
        message: 'Review my instruction prompt and JSON schema.',
        context: { action: 'review', prompt, jsonSchema },
      };
    case 'tips':
      return {
        message: 'Give me tips on how to improve my system prompt.',
        context: { action: 'tips', prompt, jsonSchema },
      };
    case 'rewrite':
      return {
        message: 'Rewrite my system prompt and JSON schema for optimal LLM performance.',
        context: { action: 'rewrite', prompt, jsonSchema },
      };
    case 'cleanEmojis':
      return {
        message: 'Clean up emojis from my system prompt.',
        context: { action: 'cleanEmojis', prompt, jsonSchema },
      };
    case 'generateSchema':
      return {
        message: 'Generate a JSON response_format based on my system prompt.',
        context: { action: 'generateSchema', prompt, jsonSchema },
      };
    case 'addDescriptions':
      return {
        message: 'Add descriptions to all fields in my JSON schema.',
        context: { action: 'addDescriptions', prompt, jsonSchema },
      };

    // Navigation actions
    case 'navigateCreate':
      return { message: null, navigateTo: '/playground' };
    case 'navigateSystem':
    case 'navigateSystemMessages':
      return { message: null, navigateTo: '/system-messages' };
    case 'openDocs':
      return { message: null, openUrl: EXTERNAL_LINKS.docs };
    case 'openNeurix':
      return { message: null, openUrl: EXTERNAL_LINKS.neurix };
    case 'navigateHome':
      return { message: null, navigateTo: '/' };

    // Help/guide actions
    case 'how-to-playground':
      return {
        message: 'How do I use the Playground? Give me a quick guide.',
        context: {},
      };
    case 'helpPickPrompt':
      return {
        message: 'Help me pick the right instruction for my use case.',
        context: { action: 'pickSystemMessage' },
      };

    default:
      return null;
  }
}
